import { Button } from "@/ds/shadcn/components/ui/button";
import { ButtonGroup } from "@/ds/shadcn/components/ui/button-group";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/ds/shadcn/components/ui/input-group";
import { cn } from "@/ds/utils";
import { MinusIcon, PlusIcon } from "lucide-react";
import { useCallback, useEffect, useState, type FC } from "react";

export interface NumericInputProps {
	value: number;
	onChange: (value: number) => void;
	maxDecimalPlaces?: number;
	minValue?: number;
	maxValue?: number;
	prefix?: string;
	className?: string;
}

export const NumericInput: FC<NumericInputProps> = props => {
	const {
		value,
		onChange,
		maxDecimalPlaces = 2,
		minValue,
		maxValue,
		prefix = "",
		className
	} = props;

	const [inputText, setInputText] = useState(value.toFixed(maxDecimalPlaces));
	const incrementedValue = maxValue === undefined ? value + 1 : Math.min(value + 1, maxValue);
	const decrementedValue = minValue === undefined ? value - 1 : Math.max(value - 1, minValue);
	const canIncrement = incrementedValue !== value;
	const canDecrement = decrementedValue !== value;

	useEffect(() => {
		setInputText(value.toFixed(maxDecimalPlaces));
	}, [value, maxDecimalPlaces]);

	const onBlur = useCallback(() => {
		let parsedValue = parseFloat(inputText);

		if (isNaN(parsedValue)) {
			setInputText(value.toFixed(maxDecimalPlaces));
			return;
		}

		if (minValue !== undefined) {
			parsedValue = Math.max(parsedValue, minValue);
		}

		if (maxValue !== undefined) {
			parsedValue = Math.min(parsedValue, maxValue);
		}

		onChange(parsedValue);
		setInputText(parsedValue.toFixed(maxDecimalPlaces));
	}, [inputText, value, minValue, maxValue, onChange, maxDecimalPlaces]);

	const onIncrement = useCallback(() => {
		if (!canIncrement) {
			return;
		}

		onChange(incrementedValue);
		setInputText(incrementedValue.toFixed(maxDecimalPlaces));
	}, [incrementedValue, canIncrement, onChange, maxDecimalPlaces]);

	const onDecrement = useCallback(() => {
		if (!canDecrement) {
			return;
		}

		onChange(decrementedValue);
		setInputText(decrementedValue.toFixed(maxDecimalPlaces));
	}, [decrementedValue, canDecrement, onChange, maxDecimalPlaces]);

	return (
		<ButtonGroup className={cn("w-full @md:w-auto", className)}>
			<Button variant="outline" disabled={!canDecrement} onClick={onDecrement}>
				<MinusIcon />
			</Button>
			<InputGroup className="grow">
				<InputGroupInput className="text-center text-sm" value={inputText} onChange={e => setInputText(e.target.value)} onBlur={onBlur} />
				{
					prefix &&
					<InputGroupAddon>{prefix}</InputGroupAddon>
				}
				{
					prefix &&
					<InputGroupAddon align="inline-end" className="opacity-0 @md:hidden">{prefix}</InputGroupAddon>
				}
			</InputGroup>
			<Button variant="outline" disabled={!canIncrement} onClick={onIncrement}>
				<PlusIcon />
			</Button>
		</ButtonGroup>
	);
};
